import { foldersConstants } from './constants';
import { foldersAPI } from '../../common/api/folders';
import { getDateString } from '../../common/utils';
import { history } from '../../common/helpers/history';
import { getFolderPath } from '../../common/routes';
import { toastActions } from '../toast/actions';
import { TOAST_MESSAGE } from '../../common/components/Toasts/Toast';
import { errorsActions } from '../errors/actions';

const getActiveFolderId = state => state?.folders?.activeFolderId;

const isStaleFolderUpdate = (state, expectedFolderId) => {
  if (expectedFolderId == null) return false;
  const activeFolderId = getActiveFolderId(state);
  if (activeFolderId == null) return false;
  return String(expectedFolderId) !== String(activeFolderId);
};

const ignoredStaleFolderUpdate = () => ({
  type: foldersConstants.IGNORE_STALE_FOLDER_UPDATE,
});

const setActiveFolder = folderId => ({
  type: foldersConstants.SET_ACTIVE_FOLDER,
  folderId,
});

// Run `task` only if the response still matches the currently-active folder. Used by
// bloc thunks whose Redux slice is unkeyed and would otherwise be overwritten by a late
// response from a previously-viewed folder.
const withStaleFolderGuard = (expectedFolderId, dispatch, getState, task) => {
  if (isStaleFolderUpdate(getState(), expectedFolderId)) {
    dispatch(ignoredStaleFolderUpdate());
    return false;
  }
  task();
  return true;
};

function getFolders(page, name, isFilterDisabled = false) {
  function request() {
    return { type: foldersConstants.GET_FOLDERS_REQUEST };
  }
  function success(data) {
    return { type: foldersConstants.GET_FOLDERS_SUCCESS, data };
  }
  function failure(error) {
    return { type: foldersConstants.GET_FOLDERS_FAILURE, error };
  }

  return dispatch => {
    dispatch(request());

    return foldersAPI.getFolders(page, name, isFilterDisabled).then(({ folders, meta }) => {
      dispatch(success({ folders, meta }));
    }, dispatch(errorsActions.handleErrors(failure)));
  };
}

function getFolderDetails(id) {
  function request() {
    return { type: foldersConstants.GET_FOLDER_DETAILS_REQUEST };
  }
  function success(folder) {
    return { type: foldersConstants.GET_FOLDER_DETAILS_SUCCESS, folder };
  }
  function failure(error) {
    return { type: foldersConstants.GET_FOLDER_DETAILS_FAILURE, error };
  }

  return (dispatch, getState) => {
    dispatch(request());

    return foldersAPI.getFolder(id).then(({ folder }) => {
      if (isStaleFolderUpdate(getState(), id)) {
        dispatch(ignoredStaleFolderUpdate());
        return;
      }

      dispatch(success(folder));
    }, dispatch(errorsActions.handleErrors(failure)));
  };
}

function createFolder(recipient) {
  function request() {
    return { type: foldersConstants.CREATE_FOLDER_REQUEST };
  }
  function success() {
    return { type: foldersConstants.CREATE_FOLDER_SUCCESS };
  }
  function failure(error) {
    return { type: foldersConstants.CREATE_FOLDER_FAILURE, error };
  }

  return dispatch => {
    dispatch(request(recipient));

    return foldersAPI
      .createFolder({
        recipientId: recipient.id,
        recipientAttributes: {
          ...recipient,
          recipientIsTheContact: recipient.recipientIsTheContact === 'true' || false,
          birthDate: recipient.birthDate && getDateString(recipient.birthDate),
        },
      })
      .then(
        ({ folder }) => {
          dispatch(success());
          history.push(getFolderPath('bloc_0', folder.id));
          dispatch(toastActions.success(TOAST_MESSAGE.success.add));
        },
        apiError => {
          const {
            response: { data: error },
          } = apiError;

          dispatch(
            errorsActions.handleErrors(
              () => failure({ errors: error.errors.recipient }),
              toastActions.error(TOAST_MESSAGE.error.add),
            ),
          ).call(null, apiError);
        },
      );
  };
}

function updateFolders(folder) {
  return { type: foldersConstants.UPDATE_FOLDERS, folder };
}

const getFolderFromBloc = (folder, expectedFolderId) => (dispatch, getState) => {
  if (isStaleFolderUpdate(getState?.(), expectedFolderId)) {
    return dispatch(ignoredStaleFolderUpdate());
  }

  return dispatch({ type: foldersConstants.GET_FOLDER_FROM_BLOC, folder });
};

export const foldersActions = {
  getFolders,
  getFolderDetails,
  createFolder,
  updateFolders,
  getFolderFromBloc,
  ignoredStaleFolderUpdate,
  isStaleFolderUpdate,
  setActiveFolder,
  withStaleFolderGuard,
};
